import { useEffect, useState } from "react";
import { Save, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/lib/content";
import { BLOCKS } from "@/components/admin/Blocks";

export default function AdminContent() {
  const { refetch } = useContent();
  const [activeKey, setActiveKey] = useState("visibility");
  const [data, setData] = useState(null);
  const [pristine, setPristine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

// Default shape for visibility — used if existing data is corrupted/missing.
const DEFAULT_VISIBILITY = {
  stats: true, benefits: true, partners: true, pricing: true, process: true,
  countries: true, vacancies: true, team: true, reviews: true, contacts: true, payment: true,
};

const isValidVisibility = (d) => d && typeof d === "object" && Object.keys(d).some((k) => k in DEFAULT_VISIBILITY);

  const load = async (key) => {
    setLoading(true);
    setData(null);  // clear stale data to avoid cross-block contamination
    try {
      const { data: doc } = await api.get(`/content/${key}`);
      let payload = doc.data;
      // Heal corrupted visibility block (e.g. hero data accidentally saved here)
      if (key === "visibility" && !isValidVisibility(payload)) {
        payload = { ...DEFAULT_VISIBILITY };
      }
      setData(payload);
      setPristine(JSON.stringify(payload));
    } catch {
      if (key === "visibility") {
        setData({ ...DEFAULT_VISIBILITY });
        setPristine(JSON.stringify({ ...DEFAULT_VISIBILITY }));
      } else {
        toast.error("Не удалось загрузить");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(activeKey); }, [activeKey]);

  const switchTo = (k) => {
    if (k === activeKey) return;
    setData(null);
    setPristine(null);
    setActiveKey(k);
  };

  const dirty = data && pristine !== null && JSON.stringify(data) !== pristine;

  const save = async () => {
    setSaving(true);
    try {
      // Sanity guard: visibility must be a flat dict of booleans (not hero/team data).
      // If something obviously wrong (e.g., contains `ru` or `en` sub-objects), refuse.
      if (activeKey === "visibility") {
        const looksRight = data && typeof data === "object" && !Array.isArray(data)
          && !("ru" in data) && !("en" in data) && !("items" in data)
          && Object.values(data).every((v) => typeof v === "boolean");
        if (!looksRight) {
          toast.error("Данные для вкладки «Видимость» некорректны. Перезагружаю…");
          load("visibility");
          setSaving(false);
          return;
        }
      }
      await api.put(`/content/${activeKey}`, { key: activeKey, data });
      toast.success("Сохранено");
      setPristine(JSON.stringify(data));
      refetch();
    } catch {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const block = BLOCKS[activeKey];
  const BlockComponent = block?.Component;

  return (
    <div className="max-w-5xl w-full">
      <div className="mb-6 sm:mb-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Редактирование контента</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Содержимое сайта</h1>
        <p className="text-sm text-muted-foreground mt-2">Меняйте тексты, заголовки, кнопки и картинки. Все изменения видны после сохранения.</p>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-4 lg:gap-6">
        {/* Sidebar of blocks */}
        <aside className="lg:sticky lg:top-4 self-start">
          <div className="lg:hidden mb-3">
            <select
              value={activeKey}
              onChange={(e) => switchTo(e.target.value)}
              className="w-full h-11 px-4 rounded-xl border border-input bg-background text-sm font-medium"
            >
              {Object.entries(BLOCKS).map(([k, b]) => (
                <option key={k} value={k}>{b.label}</option>
              ))}
            </select>
          </div>
          <div className="hidden lg:flex flex-col gap-1 p-3 rounded-2xl border border-border bg-card">
            {Object.entries(BLOCKS).map(([k, b]) => (
              <button
                key={k}
                onClick={() => switchTo(k)}
                className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeKey === k ? "bg-foreground text-background" : "hover:bg-muted text-muted-foreground hover:text-foreground"}`}
                data-testid={`content-tab-${k}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </aside>

        {/* Form area */}
        <div className="space-y-4 min-w-0">
          {/* Sticky save bar */}
          <div className="sticky top-0 z-10 -mx-4 sm:mx-0 px-4 sm:px-0 py-2.5 bg-muted/30 backdrop-blur border-b border-border lg:border-0 lg:bg-transparent lg:backdrop-blur-none flex items-center gap-2">
            <button
              onClick={() => load(activeKey)}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-border text-sm font-medium hover:bg-muted"
              title="Загрузить заново"
            >
              <RefreshCw className="size-4" />
            </button>
            {dirty && (
              <div className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertCircle className="size-3.5" /> Есть несохранённые изменения
              </div>
            )}
            <div className="flex-1" />
            <button
              onClick={save}
              disabled={saving || loading || !dirty}
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-xl bg-foreground text-background text-sm font-semibold disabled:opacity-40 hover:opacity-90"
              data-testid="content-save"
            >
              <Save className="size-4" />
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
          </div>

          {loading || !data ? (
            <div className="py-20 text-center text-sm text-muted-foreground">Загрузка...</div>
          ) : BlockComponent ? (
            <BlockComponent data={data} setData={setData} />
          ) : (
            <div className="p-8 rounded-2xl border border-border bg-card text-sm text-muted-foreground">
              Этот блок пока редактируется только через JSON.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
